import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { UpdatePersonalContactsInput } from '@/lib/employees';
import type { PersonalInfo } from '@/types/employee';

interface PersonalContactsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  personalInfo?: Partial<PersonalInfo> | null;
  onSave: (input: UpdatePersonalContactsInput) => Promise<void>;
}

type FieldKey = 'telegram' | 'whatsapp' | 'viber' | 'signal' | 'discord' | 'linkedinUrl';

const FIELDS: { key: FieldKey; label: string; placeholder: string }[] = [
  { key: 'telegram', label: 'Telegram', placeholder: '@username' },
  { key: 'whatsapp', label: 'WhatsApp', placeholder: '+380 50 123 4567' },
  { key: 'viber', label: 'Viber', placeholder: '+380 50 123 4567' },
  { key: 'signal', label: 'Signal', placeholder: '+380 50 123 4567' },
  { key: 'discord', label: 'Discord', placeholder: 'username' },
  { key: 'linkedinUrl', label: 'LinkedIn', placeholder: 'https://linkedin.com/in/…' },
];

const emptyForm: Record<FieldKey, string> = {
  telegram: '',
  whatsapp: '',
  viber: '',
  signal: '',
  discord: '',
  linkedinUrl: '',
};

export function PersonalContactsModal({
  open,
  onOpenChange,
  personalInfo,
  onSave,
}: PersonalContactsModalProps) {
  const [form, setForm] = useState<Record<FieldKey, string>>(emptyForm);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({
      telegram: personalInfo?.telegram ?? '',
      whatsapp: personalInfo?.whatsapp ?? '',
      viber: personalInfo?.viber ?? '',
      signal: personalInfo?.signal ?? '',
      discord: personalInfo?.discord ?? '',
      linkedinUrl: personalInfo?.linkedinUrl ?? '',
    });
  }, [open, personalInfo]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      // Send every field — empty string clears the value server-side.
      const payload: UpdatePersonalContactsInput = {
        telegram: form.telegram.trim() || null,
        whatsapp: form.whatsapp.trim() || null,
        viber: form.viber.trim() || null,
        signal: form.signal.trim() || null,
        discord: form.discord.trim() || null,
        linkedinUrl: form.linkedinUrl.trim() || null,
      };
      await onSave(payload);
      onOpenChange(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Personal Contacts</DialogTitle>
          <DialogDescription>
            Private messenger handles and LinkedIn URL for this employee. Visible only to users with
            the Emergency permission.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 py-4">
            {FIELDS.map(({ key, label, placeholder }) => (
              <div key={key} className="grid gap-2">
                <Label htmlFor={key}>{label}</Label>
                <Input
                  id={key}
                  value={form[key]}
                  onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                  placeholder={placeholder}
                  disabled={isSubmitting}
                />
              </div>
            ))}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
